import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

// Image Carousel Component with Single Thumbnail and Popup Modal
function ImageCarousel({ screenshots }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [slideDirection, setSlideDirection] = useState('right');

  const nextSlide = () => {
    setSlideDirection('right');
    setCurrentIndex((prevIndex) => 
      prevIndex === screenshots.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevSlide = () => {
    setSlideDirection('left');
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? screenshots.length - 1 : prevIndex - 1
    );
  };

  const goToSlide = (index) => {
    setSlideDirection(index > currentIndex ? 'right' : 'left');
    setCurrentIndex(index);
  };

  const openModal = (index) => {
    setSelectedImageIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImageIndex(null);
  };

  const nextImage = () => {
    setSelectedImageIndex((prevIndex) => 
      prevIndex === screenshots.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setSelectedImageIndex((prevIndex) => 
      prevIndex === 0 ? screenshots.length - 1 : prevIndex - 1
    );
  };

  const handleKeyDown = (e) => {
    if (!isModalOpen) return;
    
    switch (e.key) {
      case 'Escape':
        closeModal();
        break;
      case 'ArrowRight':
        nextImage();
        break;
      case 'ArrowLeft':
        prevImage();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen]);

  if (!screenshots || screenshots.length === 0) {
    return (
      <div className="aspect-video bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl flex items-center justify-center border border-blue-500/20">
        <div className="text-center text-gray-400">
          <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-lg font-medium">No screenshots available</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Single Thumbnail Carousel */}
      <div className="relative group">
        {/* Main Thumbnail */}
        <div 
          className="aspect-video bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl overflow-hidden border border-blue-500/20 cursor-pointer transform transition-all duration-500 hover:scale-105"
          onClick={() => openModal(currentIndex)}
        >
          <img
            src={screenshots[currentIndex].src}
            alt={screenshots[currentIndex].alt}
            className={`w-full h-full object-cover transition-all duration-700 ease-in-out ${
              slideDirection === 'right' ? 'animate-slide-in-right' : 'animate-slide-in-left'
            }`}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden h-full w-full items-center justify-center text-gray-400 bg-gradient-to-br from-blue-500/20 to-purple-500/20">
            <div className="text-center">
              <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-lg font-medium">{screenshots[currentIndex].alt}</p>
            </div>
          </div>
        </div>

        {/* Image Caption */}
        {screenshots[currentIndex].caption && (
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-300 font-medium">{screenshots[currentIndex].caption}</p>
          </div>
        )}

        {/* Navigation Arrows */}
        {screenshots.length > 1 && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 backdrop-blur-sm hover:scale-110"
              aria-label="Previous image"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 backdrop-blur-sm hover:scale-110"
              aria-label="Next image"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}

        {/* Image Counter */}
        {screenshots.length > 1 && (
          <div className="absolute top-4 right-4 bg-black/50 text-white text-sm px-3 py-1 rounded-full backdrop-blur-sm">
            {currentIndex + 1} / {screenshots.length}
          </div>
        )}

        {/* Click to Enlarge Hint */}
        <div className="absolute bottom-4 left-4 bg-black/50 text-white text-xs px-2 py-1 rounded backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          Click to enlarge
        </div>
      </div>

      {/* Dots Indicator */}
      {screenshots.length > 1 && (
        <div className="flex justify-center mt-6 space-x-2">
          {screenshots.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 hover:scale-125 ${
                index === currentIndex 
                  ? 'bg-blue-400 w-8 shadow-lg shadow-blue-400/50' 
                  : 'bg-gray-600 hover:bg-gray-500 w-2'
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Popup Modal */}
      {isModalOpen && selectedImageIndex !== null && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-backdrop-fade-in">
          <div className="relative max-w-4xl max-h-[90vh] w-full animate-modal-fade-in">
            {/* Close Button - Smaller and positioned outside image area */}
            <button
              onClick={closeModal}
              className="absolute -top-2 -right-2 z-20 bg-black/70 hover:bg-black/90 text-white p-2 rounded-full transition-all duration-200 backdrop-blur-sm hover:scale-110 border border-white/20"
              aria-label="Close modal"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Main Image Container */}
            <div className="relative bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl overflow-hidden border border-blue-500/20">
              <img
                src={screenshots[selectedImageIndex].src}
                alt={screenshots[selectedImageIndex].alt}
                className="w-full h-full object-contain max-h-[70vh]"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden h-full w-full items-center justify-center text-gray-400 bg-gradient-to-br from-blue-500/20 to-purple-500/20">
                <div className="text-center">
                  <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-lg font-medium">{screenshots[selectedImageIndex].alt}</p>
                </div>
              </div>

              {/* Navigation Arrows - Smaller and positioned on edges */}
              {screenshots.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all duration-200 backdrop-blur-sm hover:scale-110 border border-white/20 opacity-80 hover:opacity-100"
                    aria-label="Previous image"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  
                  <button
                    onClick={nextImage}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all duration-200 backdrop-blur-sm hover:scale-110 border border-white/20 opacity-80 hover:opacity-100"
                    aria-label="Next image"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}

              {/* Image Counter - Smaller and positioned at top */}
              {screenshots.length > 1 && (
                <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm border border-white/20">
                  {selectedImageIndex + 1} / {screenshots.length}
                </div>
              )}
            </div>

            {/* Thumbnail Navigation */}
            {screenshots.length > 1 && (
              <div className="flex justify-center mt-6 space-x-2">
                {screenshots.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`h-2 rounded-full transition-all duration-200 ${
                      index === selectedImageIndex 
                        ? 'bg-blue-400 w-8' 
                        : 'bg-gray-600 hover:bg-gray-500 w-2'
                    }`}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function CourseDetails() {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");



  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const docRef = doc(db, "courses", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setCourse({ id: docSnap.id, ...docSnap.data() });
        } else {
          setError("Course not found");
        }
      } catch (err) {
        setError("Failed to load course");
        console.error("Error fetching course:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCourse();
  }, [id]);

  // Scroll to top when this component mounts (only for new visits)
  useEffect(() => {
    // Only scroll to top if we're not returning from courses page
    const isReturningFromCourses = sessionStorage.getItem('returningFromCourses');
    
    if (!isReturningFromCourses) {
      window.scrollTo(0, 0);
      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
      }
      if (document.body) {
        document.body.scrollTop = 0;
      }
    }
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-white text-lg font-medium">Loading Course Details...</p>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-2">Course Not Found</h2>
          <p className="text-gray-300 mb-6">{error || "The course you're looking for doesn't exist."}</p>
          <button
            onClick={() => {
              sessionStorage.setItem('returningFromCourse', 'true');
              window.location.href = '/courses';
            }}
            className="btn-primary inline-flex items-center gap-2 cursor-hover"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Courses
          </button>
        </div>
      </div>
    );
  }

  // Get project data from Firebase or use default
  const hasProject = course.project && course.project.title && course.project.description;
  
  const projectData = hasProject ? course.project : {
    title: "Course Overview",
    description: "This course focuses on theoretical concepts and fundamental knowledge. While no specific project was completed, the course provided valuable insights and understanding of the subject matter.",
    screenshots: [
      { src: course.imageUrl || `/images/${course.code}.svg`, alt: "Course Overview", caption: "Course focuses on theoretical learning" }
    ],
    github: null,
    technologies: ["Course Materials", "Theoretical Concepts", "Fundamental Knowledge"],
    demoUrl: null
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      {/* Back Button */}
      <section className="pt-24 sm:pt-28 pb-8 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => {
              sessionStorage.setItem('returningFromCourse', 'true');
              window.location.href = '/courses';
            }}
            className="inline-flex items-center text-blue-300 hover:text-blue-400 transition-colors mb-6 sm:mb-8 cursor-hover text-sm sm:text-base"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Courses
          </button>

          {/* Course Header */}
          <div className="glass-dark rounded-2xl p-6 sm:p-8 mb-6 sm:mb-8">
            <div className="flex flex-col lg:flex-row items-start justify-between mb-4 sm:mb-6">
              <div className="flex-1">
                <div className="text-xs sm:text-sm font-medium text-blue-400 mb-2 sm:mb-3">{course.category}</div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4 leading-tight">
                  {course.name}
                </h1>
                {course.code && (
                  <div className="text-lg sm:text-xl text-gray-300 mb-3 sm:mb-4">{course.code}</div>
                )}
                {course.credits && (
                  <div className="text-base sm:text-lg text-gray-400">
                    <span className="font-medium">Credits:</span> {course.credits}
                  </div>
                )}
              </div>
              {course.type && (
                <span className="inline-flex items-center px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30 mt-4 lg:mt-0">
                  {course.type}
                </span>
              )}
            </div>
            
            {/* Course Banner Image */}
            <div className="h-48 sm:h-64 w-full rounded-xl overflow-hidden bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/20">
              <img
                src={course.imageUrl || `/images/${course.code}.svg`}
                alt={course.name}
                className="h-full w-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden h-full w-full items-center justify-center text-white text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-500/20 to-purple-500/20">
                {course.name}
              </div>
            </div>
          </div>

          {/* Course Summary */}
          <div className="glass-dark rounded-2xl p-6 sm:p-8 mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6">Course Summary</h2>
            <p className="text-sm sm:text-base lg:text-lg text-gray-300 leading-relaxed">{course.summary}</p>
          </div>

          {/* Project Showcase */}
          <div className="glass-dark rounded-2xl p-6 sm:p-8 mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-semibold text-white mb-6 sm:mb-8">Course Work & Projects</h2>
            
            {hasProject ? (
              // Course has a project
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                {/* Project Description */}
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">{projectData.title}</h3>
                  <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-4 sm:mb-6">{projectData.description}</p>
                  
                  {/* Technologies Used */}
                  <div className="mb-4 sm:mb-6">
                    <h4 className="text-xs sm:text-sm font-medium text-gray-400 mb-2 sm:mb-3">Technologies Used:</h4>
                    <div className="flex flex-wrap gap-2">
                      {projectData.technologies.map((tech, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-gray-500/20 text-gray-300 border border-gray-500/30"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 mt-4 sm:mt-6">
                    {projectData.github && (
                      <a
                        href={projectData.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary inline-flex items-center gap-2 cursor-hover text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3"
                      >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                        </svg>
                        View on GitHub
                      </a>
                    )}
                    {projectData.demoUrl && (
                      <a
                        href={projectData.demoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-secondary inline-flex items-center gap-2 cursor-hover text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3"
                      >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Live Demo
                      </a>
                    )}
                  </div>
                </div>

                {/* Project Screenshots - Only show if screenshots exist */}
                {projectData.screenshots && projectData.screenshots.length > 0 ? (
                  <div>
                    <h4 className="text-xs sm:text-sm font-medium text-gray-400 mb-3 sm:mb-4">Project Screenshots:</h4>
                    <ImageCarousel screenshots={projectData.screenshots} />
                  </div>
                ) : (
                  // No screenshots available - show a clean placeholder
                  <div>
                    <h4 className="text-xs sm:text-sm font-medium text-gray-400 mb-3 sm:mb-4">Project Overview:</h4>
                    <div className="aspect-video bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl flex items-center justify-center border border-blue-500/20">
                      <div className="text-center text-gray-400">
                        <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-base sm:text-lg font-medium">Project Documentation</p>
                        <p className="text-xs sm:text-sm mt-2">Focus on technologies and implementation</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Course has no project
              <div className="p-4 sm:p-6 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                <div className="flex items-start">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-xs sm:text-sm text-blue-300 leading-relaxed">
                      This course emphasizes theoretical understanding and foundational knowledge. 
                      The learning outcomes focus on conceptual mastery rather than practical project work.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Course Details Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {course.semester && (
              <div className="glass-dark rounded-xl p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-white mb-2 sm:mb-3">Semester</h3>
                <p className="text-sm sm:text-base text-gray-300">{course.semester}</p>
              </div>
            )}
            
            <div className="glass-dark rounded-xl p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-white mb-2 sm:mb-3">Category</h3>
              <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
                {course.category}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Courses Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-slate-800/20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 sm:mb-6">Explore More Courses</h2>
          <p className="text-sm sm:text-base text-gray-300 mb-6 sm:mb-8 px-4">
            Discover other courses in my academic journey and see how they connect to my overall learning path.
          </p>
          <button
            onClick={() => {
              sessionStorage.setItem('returningFromCourse', 'true');
              window.location.href = '/courses';
            }}
            className="btn-primary inline-flex items-center gap-2 cursor-hover text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4"
          >
            <span>View All Courses</span>
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        </div>
      </section>
    </div>
  );
}

export default CourseDetails;
